/**
 * PublicListSectionHeader
 *
 * Section header shown in the public artifact list's "All" view to group rows
 * by kind (Tasks / Obstructions / Notes). Renders the canonical type icon, the
 * plural kind label, and a count of the rows in that section. Fully prop-driven
 * so it works on the unauthenticated share/embed pages.
 *
 * @module components/share/public/PublicArtifactList/parts/PublicListSectionHeader
 */
import { memo } from 'react';
import { Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { FONT_WEIGHT_SEMIBOLD, ICON_SIZE_TAB } from '@/constants/ui';

import { KIND_ICONS, KIND_LABEL_KEYS } from './publicListKindMeta';
import type { PublicListKind } from './shareViewerFilterUtils';
import styles from './PublicListSectionHeader.module.css';

interface PublicListSectionHeaderProps {
  kind: PublicListKind;
  count: number;
}

export const PublicListSectionHeader = memo(function PublicListSectionHeader({
  kind,
  count,
}: PublicListSectionHeaderProps) {
  const { t } = useTranslation();
  const Icon = KIND_ICONS[kind];

  return (
    <div className={styles.header}>
      <Icon size={ICON_SIZE_TAB} />
      <Text
        component="h3"
        size="xs"
        fw={FONT_WEIGHT_SEMIBOLD}
        tt="uppercase"
        c="dimmed"
        className={styles.label}
      >
        {t(KIND_LABEL_KEYS[kind])}
      </Text>
      <span className={styles.countBadge}>{count}</span>
    </div>
  );
});
